/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import SidebarService, { StaticSidebarId } from 'cio/services/sidebar';
import { htmlSafe } from '@ember/template';
import clamp from 'cio/utils/clamp';
import { registerDestructor } from '@ember/destroyable';
import { debounce } from '@ember/runloop';

export interface ResizeEventHandler {
  value: number;
  isDragging: boolean;
}

export const CONTENT_TARGET_ID = 'sidebar-panel-content-target';

const MIN_WIDTH = 370;
const MAX_WIDTH = 700;
const INITIAL_WIDTH = 400;
const MIN_CONTENT_SIZE = 1320;
const MIN_WIDTH_AS_OVERLAY = 580; // currently because of "Form" and "Event" Camapign Trigger types, in future might shrink a little bit
const MAX_WIDTH_AS_OVERLAY = 800;
const MAX_OVERLAY_LEFT_SIDE_OFFSET = 0; // for overlay mode we don't care about extra screen content on the left

/* Limits the maximum sidebar width to allow for the page content
 * to have at minimum MIN_CONTENT_SIZE pixels of width available. */
function getMaxWidth(sidebarInOverlayMode = false) {
  let max = sidebarInOverlayMode ? MAX_WIDTH_AS_OVERLAY : MAX_WIDTH;
  let minContentSize = sidebarInOverlayMode ? MAX_OVERLAY_LEFT_SIDE_OFFSET : MIN_CONTENT_SIZE;
  return Math.min(max, window.innerWidth - minContentSize);
}

function limitResizing(
  value: number,
  { rubberBanding, sidebarInOverlayMode }: { rubberBanding?: boolean; sidebarInOverlayMode?: boolean } = {},
) {
  let maxWidth = getMaxWidth(sidebarInOverlayMode);
  let minWidth = sidebarInOverlayMode === true ? MIN_WIDTH_AS_OVERLAY : MIN_WIDTH;
  if (maxWidth < minWidth) {
    maxWidth = minWidth;
  }
  let limitingFunction = rubberBanding === true ? rubberBand : clamp;
  return limitingFunction(value, maxWidth, minWidth);
}

/* Provides feedback that the sidebar can't be resized further */
function rubberBand(value: number, max: number, min: number) {
  if (value <= max && value >= min) {
    return value;
  }

  let limit = value > max ? max : min;
  let overshoot = value - limit;
  return limit + overshoot * 0.3;
}

export default class SidebarContainer extends Component<never> {
  @service sidebar!: SidebarService;
  @tracked sidebarWidth = INITIAL_WIDTH;
  @tracked isResizing = false;

  // Used to prevent rubber banding when resizing due to window resize
  isResizingWindow = false;

  // Stores user resize value to preserve the width of the sidebar when the
  // window is resized, to a certain extent.
  userSetWidth = INITIAL_WIDTH;

  constructor(owner: unknown, args: never) {
    super(owner, args);
    window.addEventListener('resize', this.handleWindowResize);

    registerDestructor(this, () => {
      window.removeEventListener('resize', this.handleWindowResize);
    });
  }

  get containerStyles() {
    let sidebarWidth = limitResizing(this.sidebarWidth, {
      rubberBanding: this.isResizing && !this.isResizingWindow,
      sidebarInOverlayMode: this.sidebarInOverlayMode,
    });
    return htmlSafe(`--sidebar-width:${sidebarWidth}px;`);
  }

  get sidebarInOverlayMode() {
    let activeSidebarId = this.sidebar.activeSidebarId as StaticSidebarId;
    let closingSidebarId = this.sidebar.closingSidebarId as StaticSidebarId;

    // Some sidebars use full width page wrapper and sidebar as overlay, with bigger MIN_WIDTH
    if (closingSidebarId != null) {
      // we are storing closing sidebarId for shortDelay to help out with closing css transition
      return [StaticSidebarId.CampaignTrigger, StaticSidebarId.GenerateLiquidWithAi].includes(closingSidebarId);
    }
    return [StaticSidebarId.CampaignTrigger, StaticSidebarId.GenerateLiquidWithAi].includes(activeSidebarId);
  }

  @action
  handleResize(event: ResizeEventHandler) {
    if (!this.sidebar.isOpen) {
      return;
    }
    this.isResizing = event.isDragging;

    this.userSetWidth = event.value;

    this.sidebarWidth = this.userSetWidth;
  }

  // Used on the debounce on handleWindowResize
  setIsResizingFalse() {
    this.isResizing = false;
    this.isResizingWindow = false;
  }

  @action
  handleWindowResize() {
    // ignore if sidebar is closed
    if (!this.sidebar.isOpen) {
      return;
    }

    // Sets isResizing to true to disable transitions while the window is being resized
    this.isResizing = true;
    this.isResizingWindow = true;
    // isResizing is set to false after a moment when the user has stopped resizing the window
    debounce(this, this.setIsResizingFalse, 250);

    this.sidebarWidth = this.userSetWidth;
  }
}
